import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { predikatOf, predikatColor, useActiveTA } from "@/lib/academic";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import SantriPhoto from "@/components/SantriPhoto";

export default function InputNilaiPage() {
  const { user } = useAuth();
  const { active: activeTA } = useActiveTA();
  const isUstadz = user.role === "ustadz";

  const [tahunAjaran, setTahunAjaran] = useState("");
  const [semester, setSemester] = useState("ganjil");
  const [jenisUjianId, setJenisUjianId] = useState("");
  const [kelasId, setKelasId] = useState("");
  const [mapelId, setMapelId] = useState("");

  const [taList, setTaList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [jenisList, setJenisList] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);

  // load filters
  const loadFilters = useCallback(async () => {
    try {
      const reqs = [
        api.get("/tahun-ajaran"),
        api.get("/kelas", { params: { limit: 200 } }),
        api.get("/jenis-ujian", { params: { is_active: true } }),
      ];
      reqs.push(isUstadz
        ? api.get("/mata-pelajaran", { params: { only_mine: true, is_active: true } })
        : api.get("/mata-pelajaran", { params: { is_active: true } }));
      const [ta, kel, ju, mp] = await Promise.all(reqs);
      setTaList(ta.data.items || []);
      setKelasList(kel.data.items || []);
      setJenisList(ju.data.items || []);
      setMapelList(mp.data.items || []);
    } catch (_e) { /* ignore */ }
  }, [isUstadz]);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  // Set defaults from active TA
  useEffect(() => {
    if (activeTA && !tahunAjaran) {
      setTahunAjaran(activeTA.tahun_ajaran);
      setSemester(activeTA.semester);
    }
  }, [activeTA, tahunAjaran]);

  const selectedMapel = useMemo(() => mapelList.find((m) => m.id === mapelId), [mapelList, mapelId]);

  // Filter kelas list to those matching the selected mapel's tingkatan
  const kelasForMapel = useMemo(() => {
    if (!selectedMapel) return kelasList;
    return kelasList.filter((k) => k.tingkatan === selectedMapel.tingkatan);
  }, [kelasList, selectedMapel]);

  const canLoad = tahunAjaran && semester && jenisUjianId && kelasId && mapelId;

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const { data } = await api.get("/nilai/list", {
        params: {
          kelas_id: kelasId, mata_pelajaran_id: mapelId,
          jenis_ujian_id: jenisUjianId, tahun_ajaran: tahunAjaran, semester,
        },
      });
      setRows(data.items || []);
      setAlreadySaved(data.already_saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [canLoad, kelasId, mapelId, jenisUjianId, tahunAjaran, semester]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      if (patch.nilai !== undefined) {
        const n = parseInt(patch.nilai, 10);
        next.predikat = isNaN(n) ? null : predikatOf(n);
      }
      return next;
    }));
  };

  const handleSave = async () => {
    const valid = rows.filter((r) => r.nilai !== null && r.nilai !== undefined && r.nilai !== "");
    if (valid.length === 0) {
      toast.error("Belum ada nilai yang diisi.");
      return;
    }
    // Validate range
    for (const r of valid) {
      const n = parseInt(r.nilai, 10);
      if (isNaN(n) || n < 0 || n > 100) {
        toast.error(`Nilai ${r.nama} tidak valid (harus 0-100).`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.post("/nilai/bulk", {
        kelas_id: kelasId, mata_pelajaran_id: mapelId,
        jenis_ujian_id: jenisUjianId, tahun_ajaran: tahunAjaran, semester,
        entries: valid.map((r) => ({
          santri_id: r.santri_id,
          nilai: parseInt(r.nilai, 10),
          catatan: r.catatan || null,
        })),
      });
      toast.success(`Nilai tersimpan (${valid.length} santri).`);
      setAlreadySaved(true);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="input-nilai-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Nilai Ujian</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Input Nilai</h1>
        <p className="text-slate-500 text-sm">Pilih periode, kelas, dan mata pelajaran. Predikat dihitung otomatis.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Tahun Ajaran</Label>
            <Select value={tahunAjaran} onValueChange={setTahunAjaran}>
              <SelectTrigger className="mt-1 h-10" data-testid="nilai-tahun"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>
                {[...new Set(taList.map((t) => t.tahun_ajaran))].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Semester</Label>
            <Select value={semester} onValueChange={setSemester}>
              <SelectTrigger className="mt-1 h-10" data-testid="nilai-semester"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ganjil">Ganjil</SelectItem>
                <SelectItem value="genap">Genap</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Jenis Ujian</Label>
            <Select value={jenisUjianId} onValueChange={setJenisUjianId}>
              <SelectTrigger className="mt-1 h-10" data-testid="nilai-jenis"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>
                {jenisList.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-slate-500">Belum ada jenis ujian.</div>
                ) : jenisList.map((j) => <SelectItem key={j.id} value={j.id}>{j.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Mata Pelajaran</Label>
            <Select value={mapelId} onValueChange={(v) => { setMapelId(v); setKelasId(""); }}>
              <SelectTrigger className="mt-1 h-10" data-testid="nilai-mapel"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>
                {mapelList.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-slate-500">{isUstadz ? "Anda belum mengampu mapel." : "Belum ada mapel."}</div>
                ) : mapelList.map((m) => <SelectItem key={m.id} value={m.id}>{m.nama} • {m.tingkatan}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kelas</Label>
            <Select value={kelasId} onValueChange={setKelasId} disabled={!mapelId}>
              <SelectTrigger className="mt-1 h-10" data-testid="nilai-kelas"><SelectValue placeholder={mapelId ? "Pilih" : "Pilih mapel dulu"} /></SelectTrigger>
              <SelectContent>
                {kelasForMapel.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-slate-500">Tidak ada kelas pada tingkatan ini.</div>
                ) : kelasForMapel.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {!canLoad ? (
        <Card className="p-10 text-center text-slate-400 border border-dashed border-slate-200 bg-white">
          <AlertCircle className="h-6 w-6 mx-auto mb-2" />
          Lengkapi semua filter di atas untuk menampilkan daftar santri.
        </Card>
      ) : (
        <>
          {alreadySaved && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="nilai-already-saved">
              <CheckCircle2 className="h-4 w-4" />
              Nilai untuk kombinasi ini sudah tersimpan. Anda dapat mengedit dan menyimpan ulang.
            </div>
          )}
          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-slate-400">Belum ada santri aktif di kelas ini.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((r, idx) => (
                  <li key={r.santri_id} data-testid={`nilai-row-${r.santri_id}`}
                      className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 items-center hover:bg-emerald-50/30">
                    <div className="flex items-center gap-3 md:col-span-5 min-w-0">
                      <SantriPhoto path={r.foto_path} alt={r.nama} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.nama}</div>
                        <div className="text-xs text-slate-500 font-mono">{r.nis}</div>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs text-slate-500">Nilai (0-100)</Label>
                      <Input type="number" min="0" max="100" value={r.nilai ?? ""}
                             onChange={(e) => updateRow(idx, { nilai: e.target.value })}
                             data-testid={`nilai-input-${r.santri_id}`}
                             className="mt-0.5 h-10 text-center font-semibold text-lg tabular-nums" />
                    </div>
                    <div className="md:col-span-2 text-center">
                      <Label className="text-xs text-slate-500">Predikat</Label>
                      <div className="mt-1.5">
                        {r.predikat ? (
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${predikatColor(r.predikat)}`}>
                            {r.predikat}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs text-slate-500">Catatan</Label>
                      <Input value={r.catatan || ""}
                             onChange={(e) => updateRow(idx, { catatan: e.target.value })}
                             data-testid={`nilai-catatan-${r.santri_id}`}
                             className="mt-0.5 h-10 text-sm" placeholder="Opsional" />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {rows.length > 0 && (
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving}
                      data-testid="nilai-save"
                      className="bg-emerald-600 hover:bg-emerald-700 h-11 px-6 gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Simpan Nilai
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
